/**
 * workspaceWidgets.js
 *
 * Pure widget-level operations of the workspace dashboard (edit mode, templates,
 * AI result): add from the catalogue, remove, reorder, resize S/M/L, per-widget
 * settings, build widgets from a template or a list of ids. Companion of
 * `workspaceModel.js` (workspace/tab operations, storage rules).
 *
 * Every mutator returns a NEW state, or the SAME reference when nothing changed
 * (so the store can skip a save), exactly like `helpers/dashboardPreferences.js`.
 *
 * @module helpers/workspaces/workspaceWidgets
 */

import {DEFAULT_SIZE_BY_KIND, WIDGET_SIZES, resolveWidgetDefinition} from '../../config/workspaceCatalog';
import {
    BOARD_KINDS,
    MAX_WIDGETS,
    WORKSPACE_ERRORS,
    addBoard,
    isPlainObject,
    newId,
    normalizeOptions,
    normalizeWidget,
    normalizeWidgets,
    updateBoard,
} from './workspaceModel';

/**
 * Append a catalogue widget to a workspace.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} type Catalogue widget id.
 * @param {{size?: string, options?: object, random?: Function}} [env] Overrides.
 * @returns {{state: object, widget: ?object, error: ?string}} Result.
 */
export const addWidget = (state, boardId, type, {size, options, random} = {}) => {
    const board = state.boards.find((item) => item.id === boardId);

    if (!board || board.kind === BOARD_KINDS.CLASSIC) {
        return {state, widget: null, error: WORKSPACE_ERRORS.NOT_FOUND};
    }

    const definition = resolveWidgetDefinition(type);

    if (definition.kind === 'unsupported') {
        return {state, widget: null, error: WORKSPACE_ERRORS.UNKNOWN_WIDGET};
    }

    if (board.widgets.length >= MAX_WIDGETS) {
        return {state, widget: null, error: WORKSPACE_ERRORS.MAX_WIDGETS};
    }

    const widget = normalizeWidget({
        id: newId(board.widgets.map((item) => item.id), 'w', random),
        type,
        size: size || DEFAULT_SIZE_BY_KIND[definition.kind],
        options,
    });

    return {
        state: updateBoard(state, boardId, (current) => ({...current, widgets: [...current.widgets, widget]})),
        widget,
        error: null,
    };
};

/**
 * Remove a widget.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} widgetId Widget instance id.
 * @returns {object} New state (same reference when absent).
 */
export const removeWidget = (state, boardId, widgetId) => updateBoard(state, boardId, (board) => (
    board.widgets.some((widget) => widget.id === widgetId)
        ? {...board, widgets: board.widgets.filter((widget) => widget.id !== widgetId)}
        : board
));

/**
 * Move a widget to another position.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {number} from Source index.
 * @param {number} to Target index.
 * @returns {object} New state (same reference when nothing moved).
 */
export const reorderWidgets = (state, boardId, from, to) => updateBoard(state, boardId, (board) => {
    const size = board.widgets.length;

    if (from === to || from < 0 || to < 0 || from >= size || to >= size) {
        return board;
    }

    const widgets = [...board.widgets];
    const [moved] = widgets.splice(from, 1);

    widgets.splice(to, 0, moved);

    return {...board, widgets};
});

/**
 * Move a widget one step up (-1) or down (+1).
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} widgetId Widget instance id.
 * @param {number} direction -1 | +1.
 * @returns {object} New state (same reference at the edge).
 */
export const moveWidgetBy = (state, boardId, widgetId, direction) => {
    const board = state.boards.find((item) => item.id === boardId);
    const from = board ? board.widgets.findIndex((widget) => widget.id === widgetId) : -1;

    return from === -1 ? state : reorderWidgets(state, boardId, from, from + (direction < 0 ? -1 : 1));
};

/**
 * Resize a widget (S / M / L).
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} widgetId Widget instance id.
 * @param {string} size 'S' | 'M' | 'L'.
 * @returns {object} New state (same reference when invalid / unchanged).
 */
export const resizeWidget = (state, boardId, widgetId, size) => updateBoard(state, boardId, (board) => {
    if (!WIDGET_SIZES.includes(size)) {
        return board;
    }

    const target = board.widgets.find((widget) => widget.id === widgetId);

    if (!target || target.size === size) {
        return board;
    }

    return {...board, widgets: board.widgets.map((widget) => (widget.id === widgetId ? {...widget, size} : widget))};
});

/**
 * Patch a widget's per-instance settings. A `null`/`undefined`/empty value clears that key
 * (back to the workspace default).
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {string} widgetId Widget instance id.
 * @param {object} patch Partial options (`period`, `restaurantId`, `limit`).
 * @returns {object} New state (same reference when nothing changed).
 */
export const setWidgetOptions = (state, boardId, widgetId, patch) => updateBoard(state, boardId, (board) => {
    const target = board.widgets.find((widget) => widget.id === widgetId);

    if (!target || !isPlainObject(patch)) {
        return board;
    }

    const merged = {...(target.options || {})};

    Object.keys(patch).forEach((key) => {
        if (patch[key] === null || patch[key] === undefined || patch[key] === '') {
            delete merged[key];
        } else {
            merged[key] = patch[key];
        }
    });

    const options = normalizeOptions(merged);

    if (JSON.stringify(options || null) === JSON.stringify(target.options || null)) {
        return board;
    }

    return {
        ...board,
        widgets: board.widgets.map((widget) => {
            if (widget.id !== widgetId) {
                return widget;
            }

            const {options: _previous, ...rest} = widget;

            return options ? {...rest, options} : rest;
        }),
    };
});

/**
 * Turn `{type, size?, options?}` entries into widget instances: unknown and
 * disallowed widgets are skipped (a module the user cannot access never gets
 * placed), ids are fresh and the count is capped.
 *
 * @param {Array<{type: string, size?: string, options?: object}>} entries Source entries.
 * @param {{isAllowed?: (definition: object) => boolean, random?: Function, max?: number}} [env] Filter and injectables.
 * @returns {Array<object>} Widget instances.
 */
export const buildWidgets = (entries, {isAllowed = () => true, random, max = MAX_WIDGETS} = {}) => {
    const ids = [];
    const widgets = [];

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
        if (!entry || typeof entry.type !== 'string' || widgets.length >= max) {
            return;
        }

        const definition = resolveWidgetDefinition(entry.type);

        if (definition.kind === 'unsupported' || !isAllowed(definition)) {
            return;
        }

        const id = newId(ids, 'w', random);
        const widget = normalizeWidget({
            id,
            type: entry.type,
            size: entry.size || DEFAULT_SIZE_BY_KIND[definition.kind],
            options: entry.options,
        });

        if (widget) {
            ids.push(id);
            widgets.push(widget);
        }
    });

    return widgets;
};

/**
 * Widgets for an AI result: the ids the generator picked, in order, each at its
 * default size. Ids outside the allow-list are dropped (the backend already
 * filters, this is the client-side belt and braces).
 *
 * @param {string[]} ids Widget ids returned by the generator.
 * @param {{isAllowed?: Function, random?: Function, max?: number}} [env] Filter and injectables.
 * @returns {Array<object>} Widget instances.
 */
export const widgetsFromIds = (ids, env = {}) => buildWidgets(
    (Array.isArray(ids) ? ids : []).map((type) => ({type})),
    {max: 12, ...env},
);

/**
 * Apply a template as a NEW workspace (one tap).
 *
 * @param {object} state Current state.
 * @param {{id: string, icon?: string, widgets: Array<object>}} template Template (see `workspaceTemplates`).
 * @param {{name: string, isAllowed?: Function, now?: Date, random?: Function}} env Localized name + filters.
 * @returns {{state: object, board: ?object, error: ?string}} Result.
 */
export const applyTemplateAsBoard = (state, template, {name, isAllowed, now, random} = {}) => addBoard(state, {
    name,
    widgets: buildWidgets(template?.widgets, {isAllowed, random}),
    templateId: template?.id || null,
    icon: template?.icon || null,
}, {now, random});

/**
 * Replace the widgets of an existing workspace (AI result applied to the open board).
 * The classic dashboard is never touched.
 *
 * @param {object} state Current state.
 * @param {string} boardId Board id.
 * @param {Array<object>} widgets Ready widget instances.
 * @returns {object} New state.
 */
export const setBoardWidgets = (state, boardId, widgets) => updateBoard(state, boardId, (board) => (
    board.kind === BOARD_KINDS.CLASSIC ? board : {...board, widgets: normalizeWidgets(widgets)}
));
